import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

/**
 * The one chip / status pill. Tones follow the brand roles: accent (violet, default), primary (orange),
 * success / warning / danger for status, steel + neutral for quiet metadata. Domain status maps (task status,
 * meeting status, ...) should resolve to a tone here rather than hand-rolling colour classes.
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium font-poppins',
  {
    variants: {
      variant: {
        default: 'bg-accent-soft text-accent-text',
        accent: 'bg-accent-soft text-accent-text',
        primary: 'bg-primary-soft text-primary-text',
        success: 'bg-success/15 text-success-text',
        warning: 'bg-warning/15 text-warning-text',
        danger: 'bg-danger/15 text-danger-text',
        steel: 'bg-steel/15 text-steel-text',
        neutral: 'bg-ui-hover text-ui-text2',
        dark: 'bg-chrome-from text-chrome-text',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

const Badge: React.FC<BadgeProps> = ({ className, variant, ...props }) => (
  <span className={cn(badgeVariants({ variant }), className)} {...props} />
);

export { Badge, badgeVariants };
